import React, { useEffect, useCallback } from 'react';
import ProgressBar from './ProgressBar';
import QuestionNavigator from './QuestionNavigator';
import ExamControls from './ExamControls';
import QuestionCard from './QuestionCard';
import useExamTimer from '../hooks/useExamTimer';
import useExamState from '../hooks/useExamState';
import './ExamView.css';

const ExamView = ({ 
  questions: initialQuestions, 
  selectedAssessment, 
  timeLimit = 60,
  onSubmit,
  shuffleQuestions = true,
  shuffleChoices = true
}) => {
  const {
    questions,
    currentQuestion,
    answers,
    flaggedQuestions,
    isNavigatorVisible,
    currentQuestionData,
    canGoNext,
    canGoPrevious,
    isLastQuestion,
    initializeExam,
    selectAnswer,
    toggleFlag,
    goToQuestion,
    goToNext,
    goToPrevious,
    toggleNavigator,
    getExamSummary,
    getSubmissionData
  } = useExamState();

  const {
    timeRemaining,
    isPaused,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    setOnTimeUp
  } = useExamTimer(timeLimit);

  // Initialize exam when questions are loaded (only once)
  const initializedRef = React.useRef(false);
  
  useEffect(() => {
    if (initialQuestions && initialQuestions.length > 0 && !initializedRef.current) {
      initializeExam(initialQuestions, shuffleQuestions);
      startTimer(timeLimit);
      initializedRef.current = true;
    }
  }, [initialQuestions, initializeExam, startTimer, timeLimit, shuffleQuestions]);

  // Prevent accidental navigation away from exam
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'WARNING: You are in the middle of an assessment. Leaving will NOT reset your attempt. Are you sure you want to leave?';
      return e.returnValue;
    };

    const handlePopState = (e) => {
      const confirmLeave = window.confirm(
        'WARNING: You are in the middle of an assessment.\n\n' +
        'Leaving will NOT reset your attempt - you will not be able to retake this assessment.\n\n' +
        'Are you sure you want to leave?'
      );
      if (!confirmLeave) {
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Prevent copy/paste operations
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    const handleCut = (e) => {
      e.preventDefault();
      return false;
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('contextmenu', handleContextMenu);
    
    // Push a state to handle back button
    window.history.pushState(null, '', window.location.pathname);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const handleSubmit = useCallback(() => {
    console.log('=== HANDLE SUBMIT CALLED ===');
    const summary = getExamSummary();
    console.log('Exam summary:', summary);
    const unansweredCount = summary.unansweredQuestions;
    
    if (unansweredCount > 0 && timeRemaining > 0) {
      const confirmMessage = `You have ${unansweredCount} unanswered question${unansweredCount > 1 ? 's' : ''}. Are you sure you want to submit?`;
      if (!window.confirm(confirmMessage)) {
        console.log('User cancelled submission due to unanswered questions');
        return;
      }
    }
    
    console.log('Stopping timer and getting submission data...');
    stopTimer();
    const submissionData = getSubmissionData();
    console.log('Submission data from exam state:', submissionData);
    console.log('Calling onSubmit with submission data...');
    onSubmit(submissionData);
  }, [getExamSummary, getSubmissionData, timeRemaining, stopTimer, onSubmit]);

  // Handle time up
  const handleTimeUp = useCallback(() => {
    alert('⏰ Time is up! Submitting exam automatically.');
    handleSubmit();
  }, [handleSubmit]);

  useEffect(() => {
    setOnTimeUp(handleTimeUp);
  }, [setOnTimeUp, handleTimeUp]);

  const handleAnswerSelect = useCallback((answer) => {
    selectAnswer(currentQuestion, answer);
  }, [selectAnswer, currentQuestion]);

  const handlePause = useCallback(() => {
    pauseTimer();
  }, [pauseTimer]);

  const handleResume = useCallback(() => {
    resumeTimer();
  }, [resumeTimer]);

  // Show loading if no questions
  if (!questions || questions.length === 0) {
    return (
      <div className="exam-view loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h2>Loading Exam...</h2>
          <p>Please wait while we prepare your assessment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`exam-view ${isPaused ? 'paused' : ''}`}>
      {/* Progress Bar */}
      <ProgressBar 
        current={currentQuestion + 1}
        total={questions.length}
        timeRemaining={timeRemaining}
        timeLimit={timeLimit}
      />

      {/* Question Navigator */}
      <QuestionNavigator
        questions={questions}
        currentQuestion={currentQuestion}
        answers={answers}
        flaggedQuestions={flaggedQuestions}
        onQuestionSelect={goToQuestion}
        onToggleFlag={toggleFlag}
        isVisible={isNavigatorVisible}
        onToggleVisibility={toggleNavigator}
      />

      {/* Main Content */}
      <div className="exam-content">
        <div className="exam-header">
          <h1>{selectedAssessment}</h1>
        </div>

        {isPaused && (
          <div className="pause-overlay">
            <div className="pause-message">
              <h2>⏸️ Exam Paused</h2>
              <p>Click "Resume Exam" to continue.</p>
              <button className="btn btn-primary" onClick={handleResume}>
                Resume Exam
              </button>
            </div>
          </div>
        )}

        {!isPaused && (
          <QuestionCard
            question={currentQuestionData}
            questionIndex={currentQuestion}
            totalQuestions={questions.length}
            selectedAnswer={answers[currentQuestion]}
            onAnswerSelect={handleAnswerSelect}
            shuffleChoices={shuffleChoices}
          />
        )}
      </div>

      {/* Exam Controls */}
      <ExamControls
        currentQuestion={currentQuestion}
        totalQuestions={questions.length}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onSubmit={handleSubmit}
        canGoBack={canGoPrevious}
        canGoNext={canGoNext}
        showSubmit={isLastQuestion || Object.keys(answers).length === questions.length}
        isPaused={isPaused}
        onPause={handlePause}
        onResume={handleResume}
      />

      {/* Keyboard shortcuts info */}
      <div className="keyboard-shortcuts">
        <small>
          Shortcuts: ← Previous | → Next | Space Flag | Enter Submit
        </small>
      </div>
    </div>
  );
};

export default ExamView;